import React, { useState } from 'react';
import { BarChart3, TrendingUp, TrendingDown, DollarSign, Calendar, Filter } from 'lucide-react';
import { usePageHeader } from '../contexts/PageHeaderContext';
import PageHeader from '../components/PageHeader';
import { showToast } from '../utils/toast';

// Page Header configuration
const PAGE_HEADER_CONFIG = {
  breadcrumbs: [{ label: 'Dashboard', path: '/' }, { label: 'Analytics' }],
  title: 'Analytics',
  icon: '📊',
  description: 'Comprehensive analytics and insights for your projects.',
  actionButton: {
    icon: '📥',
    label: 'Export Report',
    onClick: () => showToast('Report exported successfully!', 'success')
  }
};

// Sample analytics data
const analyticsData = [
  { period: 'Jan', value: 45000, change: +12 },
  { period: 'Feb', value: 52000, change: +15 },
  { period: 'Mar', value: 48000, change: -8 },
  { period: 'Apr', value: 61000, change: +27 },
  { period: 'May', value: 55000, change: -10 },
  { period: 'Jun', value: 67000, change: +22 },
];

const metrics = [
  { label: 'Total Revenue', value: '$287,000', change: '+18%', trend: 'up', icon: DollarSign },
  { label: 'Active Projects', value: '24', change: '+5', trend: 'up', icon: BarChart3 },
  { label: 'Completion Rate', value: '87%', change: '+3%', trend: 'up', icon: TrendingUp },
  { label: 'Avg. Duration', value: '45 days', change: '-5 days', trend: 'down', icon: Calendar },
];

const Analytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('6M');
  usePageHeader(PAGE_HEADER_CONFIG);

  const maxValue = Math.max(...analyticsData.map(d => d.value));

  return (
    <div className="space-y-6">
      <PageHeader {...PAGE_HEADER_CONFIG} />
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          const TrendIcon = metric.trend === 'up' ? TrendingUp : TrendingDown;
          
          return (
            <div
              key={index}
              className="rounded-xl p-6 shadow-sm transition-all duration-300 hover:shadow-md"
              style={{ 
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)'
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <div 
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: 'var(--color-primary-light)' }}
                >
                  <Icon className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
                </div>
                <div className={`flex items-center text-sm font-medium ${
                  metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  <TrendIcon className="w-4 h-4 mr-1" />
                  {metric.change}
                </div>
              </div>
              <div>
                <p 
                  className="text-sm mb-1"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {metric.label}
                </p>
                <p 
                  className="text-2xl font-bold"
                  style={{ color: 'var(--color-text)' }}
                >
                  {metric.value}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Chart Section */}
      <div 
        className="rounded-xl p-6 shadow-sm transition-colors duration-300"
        style={{ 
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)'
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 
              className="text-lg font-semibold mb-1"
              style={{ color: 'var(--color-text)' }}
            >
              Revenue Trend
            </h3>
            <p 
              className="text-sm"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Last 6 months performance
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {['1M', '3M', '6M', '1Y'].map((period) => (
              <button
                key={period}
                onClick={() => {
                  setSelectedPeriod(period);
                  showToast(`Filtered to ${period}`, 'info');
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedPeriod === period
                    ? 'text-white'
                    : 'text-gray-600'
                }`}
                style={
                  selectedPeriod === period
                    ? { backgroundColor: 'var(--color-primary)' }
                    : {
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-text-secondary)'
                      }
                }
              >
                {period}
              </button>
            ))}
          </div>
        </div>

        {/* Bar Chart */}
        <div className="flex items-end justify-between space-x-4 h-64">
          {analyticsData.map((item, index) => {
            const height = (item.value / maxValue) * 100;
            const isPositive = item.change > 0;
            
            return (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div className="w-full flex flex-col items-center mb-2">
                  <div 
                    className="w-full rounded-t-lg transition-all duration-300 hover:opacity-80"
                    style={{
                      height: `${height}%`,
                      backgroundColor: isPositive ? 'var(--color-success)' : 'var(--color-danger)',
                      minHeight: '20px',
                    }}
                  />
                  <div className={`text-xs font-medium mt-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {item.change > 0 ? '+' : ''}{item.change}%
                  </div>
                </div>
                <div 
                  className="text-xs font-medium mt-2"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {item.period}
                </div>
                <div 
                  className="text-xs mt-1"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  ${(item.value / 1000).toFixed(0)}k
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div 
          className="rounded-xl p-6 shadow-sm transition-colors duration-300"
          style={{ 
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)'
          }}
        >
          <h3 
            className="text-lg font-semibold mb-4"
            style={{ color: 'var(--color-text)' }}
          >
            Top Performing Projects
          </h3>
          <div className="space-y-3">
            {[
              { name: 'Project Alpha', progress: 92, revenue: '$125k' },
              { name: 'Project Beta', progress: 87, revenue: '$98k' },
              { name: 'Project Gamma', progress: 75, revenue: '$76k' },
            ].map((project, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span 
                    className="text-sm font-medium"
                    style={{ color: 'var(--color-text)' }}
                  >
                    {project.name}
                  </span>
                  <span 
                    className="text-sm font-semibold"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    {project.revenue}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${project.progress}%`,
                      backgroundColor: 'var(--color-primary)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div 
          className="rounded-xl p-6 shadow-sm transition-colors duration-300"
          style={{ 
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)'
          }}
        >
          <h3 
            className="text-lg font-semibold mb-4"
            style={{ color: 'var(--color-text)' }}
          >
            Recent Insights
          </h3>
          <div className="space-y-4">
            {[
              { insight: 'Revenue increased by 18% this quarter', type: 'success' },
              { insight: '5 new projects started this month', type: 'info' },
              { insight: 'Average completion time reduced by 5 days', type: 'success' },
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-start space-x-3 p-3 rounded-lg"
                style={{ backgroundColor: 'var(--color-surface-hover)' }}
              >
                <div 
                  className="w-2 h-2 rounded-full mt-2"
                  style={{ backgroundColor: `var(--color-${item.type})` }}
                />
                <p 
                  className="text-sm flex-1"
                  style={{ color: 'var(--color-text)' }}
                >
                  {item.insight}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
